<!--
	EmailCapture — waitlist capture (02 §6, 06 §4). Works as a plain form POST with
	no JS (the /api/waitlist endpoint accepts form-encoded and JSON); with JS it
	intercepts, validates, and on success swaps to a STATUS / CONFIRMED readout. The
	response is identical whether the email is new or already on the list (no
	enumeration — the server guarantees this; the UI just shows success either way).

	`qualifier` adds the one extra question the paid-traffic landing page asks
	("Do you run a plotter or 3D scanner today?") as a required radio pair named
	`equipment`, so the no-JS form post and the JSON path carry the same answer.
	A "yes" is what the ad pilot counts as a qualified lead (plan/14 blocker 4),
	so it also fires a second, narrower event next to the usual generate_lead.
-->
<script lang="ts">
	import Field from './Field.svelte';
	import Button from './Button.svelte';
	import Readout from './Readout.svelte';
	import { trackEvent } from '$lib/analytics';

	type Source = 'hero' | 'pricing' | 'soldout' | 'thanks' | 'landing';
	type Status = 'idle' | 'submitting' | 'success' | 'error';
	type Equipment = 'yes' | 'not_yet';

	let {
		source = 'hero',
		label = 'Email',
		cta = 'Notify me',
		qualifier,
		class: klass = ''
	}: {
		source?: Source;
		label?: string;
		cta?: string;
		/** Optional single qualifying question rendered as two radios (`equipment`). */
		qualifier?: { legend: string; yes: string; notYet: string };
		class?: string;
	} = $props();

	let email = $state('');
	let equipment = $state<Equipment | ''>('');
	let status = $state<Status>('idle');
	let message = $state('');

	const valid = (e: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e);

	async function handleSubmit(e: SubmitEvent) {
		e.preventDefault();
		if (!valid(email)) {
			status = 'error';
			message = 'That email looks incomplete.';
			return;
		}
		if (qualifier && !equipment) {
			status = 'error';
			message = 'Pick one answer above.';
			return;
		}
		status = 'submitting';
		try {
			const res = await fetch('/api/waitlist', {
				method: 'POST',
				headers: { 'content-type': 'application/json' },
				body: JSON.stringify({ email, source, ...(equipment ? { equipment } : {}) })
			});
			if (res.ok) {
				const { eventId } = await res.json();
				status = 'success';
				message = "You're on the list.";
				const props = { placement: source, ...(equipment ? { equipment } : {}) };
				trackEvent('Waitlist Signup', { props, eventId }, 'generate_lead');
				if (equipment === 'yes') trackEvent('Qualified Lead', { props }, 'qualified_lead');
			} else {
				status = 'error';
				message = 'Something went wrong. Try again.';
			}
		} catch {
			status = 'error';
			message = 'Network error. Try again.';
		}
	}
</script>

{#if status === 'success'}
	<div class={klass} role="status">
		<Readout label="Status" value="Confirmed" accent />
		<p class="mt-2 text-body text-bone-500">{message}</p>
	</div>
{:else}
	<form class="ec {klass}" method="POST" action="/api/waitlist" onsubmit={handleSubmit}>
		<input type="hidden" name="source" value={source} />
		{#if qualifier}
			<fieldset class="ec__q">
				<legend class="ec__legend">{qualifier.legend}</legend>
				<div class="ec__choices">
					<label class="ec__choice">
						<input type="radio" name="equipment" value="yes" required bind:group={equipment} />
						<span>{qualifier.yes}</span>
					</label>
					<label class="ec__choice">
						<input type="radio" name="equipment" value="not_yet" required bind:group={equipment} />
						<span>{qualifier.notYet}</span>
					</label>
				</div>
			</fieldset>
		{/if}
		<div class="ec__row">
			<Field
				class="ec__field"
				{label}
				name="email"
				type="email"
				placeholder="you@shop.com"
				autocomplete="email"
				required
				bind:value={email}
				error={status === 'error' ? message : undefined}
			/>
			<!-- Ghost, never primary: this sits a few inches from the actual buy button,
			     and the accent fill is reserved for the one action that matters. The
			     waitlist is the consolation prize; it shouldn't shout the same volume. -->
			<Button type="submit" variant="ghost" loading={status === 'submitting'} class="ec__btn">
				{status === 'submitting' ? 'Adding…' : cta}
			</Button>
		</div>
	</form>
{/if}

<style>
	.ec__row {
		display: flex;
		flex-wrap: wrap;
		align-items: end;
		gap: 0.75rem;
	}
	.ec :global(.ec__field) {
		flex: 1 1 16rem;
	}
	.ec :global(.ec__btn) {
		flex: none;
	}
	.ec__q {
		margin: 0 0 1rem;
		padding: 0;
		border: 0;
		min-width: 0;
	}
	.ec__legend {
		padding: 0;
		margin-bottom: 0.5rem;
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.04em;
		text-transform: uppercase;
		color: var(--color-ink-400);
	}
	.ec__choices {
		display: flex;
		flex-wrap: wrap;
		gap: 0.5rem;
	}
	.ec__choice {
		display: inline-flex;
		align-items: center;
		gap: 0.5rem;
		min-height: 2.75rem;
		padding-inline: 0.875rem;
		border: 1px solid var(--color-line-strong);
		border-radius: var(--radius-md);
		background: var(--color-surface);
		color: var(--color-ink-900);
		font-size: var(--text-body);
		cursor: pointer;
		transition:
			border-color var(--dur-fast) var(--ease-out),
			box-shadow var(--dur-fast) var(--ease-out);
	}
	.ec__choice:hover {
		border-color: var(--color-ink-300);
	}
	.ec__choice:has(input:checked) {
		border-color: var(--color-cut-600);
		box-shadow: 0 0 0 3px color-mix(in srgb, var(--color-cut-400) 35%, transparent);
	}
	.ec__choice input {
		accent-color: var(--color-cut-600);
	}
	:global(.sec--dark) .ec__legend,
	:global(.app-surface) .ec__legend {
		color: var(--color-bone-500);
	}
	:global(.sec--dark) .ec__choice,
	:global(.app-surface) .ec__choice {
		background: var(--color-app-800);
		border-color: var(--color-app-700);
		color: var(--color-bone-100);
	}
</style>
